import { ActionIcon, Box, Button, Card, Container, Grid, Group, Paper, Skeleton, Stack, Text, Title } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import * as yup from "yup";
import { PartnerApi, sectorApi } from "../../../apis";
import { Form } from "../../../components/form";
import { SelectField } from "../../../components/form/select-field";
import { TextField } from "../../../components/form/text-field";
import { TextareaField } from "../../../components/form/textarea-field";
import { PageLayout } from "../../../components/layout";
import { IGeneral } from "../../../interfaces/IGeneral";
import { resolveAdminError } from "../../../utils/admin-errors";
import { Message } from "../../../utils/message";
import classes from "./index.module.css";

// Section wrapper: titled, bordered Card with short helper text and consistent
// internal padding. Keeps the three logical groups (main / contact / address)
// visually separated instead of floating bare on the page.
const SectionCard = ({ title, description, children }: { title: string; description: string; children: ReactNode }) => (
  <Card withBorder radius="md" shadow="sm" p="lg">
    <Stack gap="md">
      <Box>
        <Title order={5}>{title}</Title>
        <Text size="sm" c="dimmed">
          {description}
        </Text>
      </Box>
      {children}
    </Stack>
  </Card>
);

// Org-info (mainInfo) form, mirroring de-partner-admin's mainInfo-form +
// mainInfo-identity-section. Loads the tenant's partner via GET /prt/my/org and
// saves via PUT /prt/my/org (PartnerLogic.update -> Partner + Business). The
// identity fields (partnerType / legalEntityType / equityType / classification /
// businessSectorId / regNumber) are collected here because a freshly-provisioned
// tenant shell has them all null — there is nothing to carry through, and the
// backend Joi requires valid enum values (it rejects ""). Enum option lists come
// from the /prt/general/init redux store (loaded on app start), NOT hardcoded.
// province/district/khoroo are cascading zip-code Selects sourced from the same
// store (state.general.zipCodes); a freshly-provisioned shell has them null, so
// they must be selectable (the backend Joi requires valid zip codes).

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormValues = Record<string, any>;

const PHONE_REGEX = /^[9|8|6]{1}[0-9]{7}$/;
const PHONE_MSG = "Утасны дугаарын формат буруу байна!";
const EMAIL_MSG = "И-мэйл хаяг буруу байна";
const REQUIRED = "Заавал бөглөнө үү";

const schema = yup.object({
  partnerType    : yup.string().required("Байгууллагын төрөл сонгоно уу"),
  // CITIZEN (иргэн) uses business sector instead of corporate legal-entity /
  // equity / classification, so those are required only for COMPANY.
  legalEntityType: yup.string().when(["partnerType"], ([type]) =>
    type === "CITIZEN"
      ? yup.string().nullable()
      : yup.string().required("Хуулийн этгээдийн төрөл сонгоно уу"),
  ),
  equityType: yup.string().when(["partnerType"], ([type]) =>
    type === "CITIZEN"
      ? yup.string().nullable()
      : yup.string().required("Өмчийн хэлбэр сонгоно уу"),
  ),
  classification: yup.string().when(["partnerType"], ([type]) =>
    type === "CITIZEN"
      ? yup.string().nullable()
      : yup.string().required("Ангилал сонгоно уу"),
  ),
  // CITIZEN: "Үйл ажиллагааны чиглэл" = business sector (required); COMPANY keeps classification.
  businessSectorId: yup.string().when(["partnerType"], ([type]) =>
    type === "CITIZEN"
      ? yup.string().required("Үйл ажиллагааны чиглэл сонгоно уу")
      : yup.string().nullable(),
  ),
  businessName   : yup.string().required("Байгууллагын нэр оруулна уу"),
  businessNameEng: yup.string().transform((v) => (v ? v : null)).nullable(),
  regNumber      : yup.string().when(["partnerType"], ([type]) =>
    type === "CITIZEN"
      ? yup
        .string()
        .required("Регистрийн дугаар оруулна уу")
        .matches(/^[А-Яа-я|Үү|Өө|Ёё]{2}\d{8}$/, "Регистрийн дугаарын формат буруу байна")
      : yup
        .string()
        .required("Регистрийн дугаар оруулна уу")
        .matches(/^\d{7}$/, "Байгууллагын регистр 7 оронтой тоо байна"),
  ),
  email          : yup.string().email(EMAIL_MSG).required("И-мэйл хаяг оруулна уу"),
  email2         : yup.string().transform((v) => (v ? v : null)).email(EMAIL_MSG).nullable(),
  phone          : yup.string().matches(PHONE_REGEX, PHONE_MSG).required("Утасны дугаар оруулна уу"),
  phone2         : yup.string().transform((v) => (v ? v : null)).matches(PHONE_REGEX, PHONE_MSG).nullable(),
  province       : yup.string().required("Аймаг/Хот сонгоно уу"),
  district       : yup.string().required("Сум/Дүүрэг сонгоно уу"),
  khoroo         : yup.string().required("Баг/Хороо сонгоно уу"),
  khotkhonBair   : yup.string().required(REQUIRED),
  khoroolol      : yup.string().required(REQUIRED),
  khashaaDavkhar : yup.string().required(REQUIRED),
  khaalgaDugaar  : yup.string().required(REQUIRED),
  businessAddress: yup.string().required(REQUIRED),
});

const nullIfEmpty = (v: unknown) => (v === "" || v === undefined || v === null ? null : v);

const PARTNER_TYPE_OPTIONS = [
  { value: "COMPANY", label: "Байгууллага" },
  { value: "CITIZEN", label: "Иргэн" },
];

const SettingsProfile = () => {
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Enum option lists from /prt/general/init (bootstrapped into the store on app
  // load — App.tsx dispatches GeneralApi.init()). Same source as de-partner-admin.
  const { legalEntityTypes, equityTypes, partnerClassifications, zipCodes } = useSelector(
    (state: { general: IGeneral }) => state.general,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: me, isLoading, mutate } = useSWR<any>("swr.settings.my-org", () => PartnerApi.getMyOrg());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: sectors } = useSWR<any[]>("swr.settings.sectors", () =>
    sectorApi.select({ query: "", parentId: "", limit: "" })
  );

  const partner = me?.partner ?? null;
  const sectorOptions = (sectors ?? []).map((s) => ({ value: s.id, label: s.name }));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const legalEntityOptions = (legalEntityTypes ?? []).map((c: any) => ({ value: c.code, label: c.name }));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const equityOptions = (equityTypes ?? []).map((c: any) => ({ value: c.code, label: c.name }));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const classificationOptions = (partnerClassifications ?? []).map((c: any) => ({ value: c.code, label: c.name }));

  const initialValues: FormValues = {
    // A tenant is normally an organization; default a shell (type=null) to COMPANY.
    partnerType    : partner?.type ?? "COMPANY",
    legalEntityType: partner?.legalEntityType ?? "",
    equityType     : partner?.equityType ?? "",
    country        : partner?.country ?? "Монгол",
    classification : partner?.classification ?? "",
    businessSectorId: partner?.businessSectorId ?? "",
    logo           : partner?.logo ?? "",
    businessName   : partner?.businessName ?? "",
    businessNameEng: partner?.businessNameEng ?? "",
    regNumber      : partner?.regNumber ?? "",
    web            : partner?.web ?? "",
    email          : partner?.email ?? "",
    email2         : partner?.email2 ?? "",
    phone          : partner?.phone ?? "",
    phone2         : partner?.phone2 ?? "",
    fbLink         : partner?.fbLink ?? "",
    province       : partner?.province ?? "",
    district       : partner?.district ?? "",
    khoroo         : partner?.khoroo ?? "",
    khotkhonBair   : partner?.khotkhonBair ?? "",
    khoroolol      : partner?.khoroolol ?? "",
    khashaaDavkhar : partner?.khashaaDavkhar ?? "",
    khaalgaDugaar  : partner?.khaalgaDugaar ?? "",
    businessAddress: partner?.businessAddress ?? "",
    locationLat    : partner?.locationLat ?? "",
    locationLng    : partner?.locationLng ?? "",
  };

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    try {
      const isCitizen = values.partnerType === "CITIZEN";
      const payload: FormValues = {
        ...values,
        // Never send "" for an enum field — citizens have no legal-entity/equity.
        legalEntityType: isCitizen ? null : values.legalEntityType,
        equityType     : isCitizen ? null : values.equityType,
        logo           : nullIfEmpty(values.logo),
        businessNameEng: nullIfEmpty(values.businessNameEng),
        web            : nullIfEmpty(values.web),
        email2         : nullIfEmpty(values.email2),
        phone2         : nullIfEmpty(values.phone2),
        fbLink         : nullIfEmpty(values.fbLink),
        locationLat    : nullIfEmpty(values.locationLat),
        locationLng    : nullIfEmpty(values.locationLng),
      };
      // Only citizens edit the business sector here; for companies omit it so the
      // backend never wipes an existing sector (it cascades only when sent).
      if (isCitizen) {
        payload.businessSectorId = nullIfEmpty(values.businessSectorId);
      } else {
        delete payload.businessSectorId;
      }
      await PartnerApi.updateMyOrg(payload);
      Message.success("Мэдээлэл амжилттай хадгалагдлаа");
      mutate();
    } catch (err) {
      Message.error(resolveAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout loading={isLoading}>
      <Container size="md" px={0}>
        <Stack gap="lg">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              radius="md"
              mt={4}
              onClick={() => navigate(-1)}
              aria-label="Буцах">
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Box>
              <Title order={3}>Миний мэдээлэл</Title>
              <Text size="sm" c="dimmed">
                Байгууллагын үндсэн мэдээлэл
              </Text>
            </Box>
          </Group>

          {!isLoading && !partner ? (
            <Card withBorder radius="md" p="lg">
              <Text c="dimmed">
                Байгууллагын мэдээлэл олдсонгүй. Энэ хуудас нь байгууллагатай холбоотой хэрэглэгчийн мэдээллийг харуулна.
              </Text>
            </Card>
          ) : isLoading ? (
        <Grid gap="md">
          <Grid.Col span={{ xs: 12, md: 6 }}><Skeleton height={56} radius="sm" /></Grid.Col>
          <Grid.Col span={{ xs: 12, md: 6 }}><Skeleton height={56} radius="sm" /></Grid.Col>
          <Grid.Col span={12}><Skeleton height={56} radius="sm" /></Grid.Col>
        </Grid>
      ) : (
        <Form initialValues={initialValues} validationSchema={schema} onSubmit={onSubmit}>
          {({ values, setFieldValue, errors }) => {
            const isCitizen = values.partnerType === "CITIZEN";
            // Pristine guard: nothing changed vs. the loaded record -> nothing to save.
            const isDirty = Object.keys(initialValues).some(
              (k) => String(values[k] ?? "") !== String((initialValues as FormValues)[k] ?? ""),
            );
            const hasErrors = Object.keys(errors ?? {}).length > 0;
            // Cascading zip-code options (mirror de-partner-admin mainInfo-address-section).
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const provinceOptions = (zipCodes ?? []).filter((z: any) => z.parent === "0").map((z: any) => ({ value: z.code, label: z.name }));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const districtOptions = (zipCodes ?? []).filter((z: any) => z.parent === values.province).map((z: any) => ({ value: z.code, label: z.name }));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const khorooOptions = (zipCodes ?? []).filter((z: any) => z.parent === values.district).map((z: any) => ({ value: z.code, label: z.name }));
            return (
              <Stack gap="lg">
                <SectionCard
                  title="Үндсэн мэдээлэл"
                  description="Байгууллагын төрөл, нэр болон бүртгэлийн мэдээлэл">
                  <Grid gap="md">
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <SelectField
                      name="partnerType"
                      label="Байгууллагын төрөл"
                      placeholder="Сонгох"
                      options={PARTNER_TYPE_OPTIONS}
                      clearable={false}
                      required
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField
                      name="regNumber"
                      label={isCitizen ? "Регистрийн дугаар" : "Регистрийн дугаар (ТТД)"}
                      placeholder={isCitizen ? "УБ12345678" : "Регистрийн дугаар"}
                      maxLength={isCitizen ? 10 : 7}
                      required
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="businessName" label={isCitizen ? "Иргэний нэр" : "Байгууллагын нэр"} placeholder="Нэр" required />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="businessNameEng" label="Нэр (латин)" placeholder="Latin name" />
                  </Grid.Col>
                  {!isCitizen && (
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                      <SelectField
                        name="legalEntityType"
                        label="Хуулийн этгээдийн төрөл"
                        placeholder="Сонгох"
                        options={legalEntityOptions}
                        searchable
                        required
                      />
                    </Grid.Col>
                  )}
                  {!isCitizen && (
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                      <SelectField
                        name="equityType"
                        label="Өмчийн хэлбэр"
                        placeholder="Сонгох"
                        options={equityOptions}
                        searchable
                        required
                      />
                    </Grid.Col>
                  )}
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    {isCitizen ? (
                      <SelectField
                        name="businessSectorId"
                        label="Үйл ажиллагааны чиглэл"
                        placeholder="Сонгох"
                        options={sectorOptions}
                        searchable
                        required
                      />
                    ) : (
                      <SelectField
                        name="classification"
                        label="Ангилал"
                        placeholder="Сонгох"
                        options={classificationOptions}
                        searchable
                        required
                      />
                    )}
                  </Grid.Col>
                  </Grid>
                </SectionCard>

                <SectionCard title="Холбоо барих" description="И-мэйл болон утасны мэдээлэл">
                  <Grid gap="md">
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="email" label="И-мэйл" placeholder="email@company.mn" required />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="email2" label="И-мэйл 2" placeholder="email@company.mn" />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="phone" label="Утас" placeholder="99112233" required maxLength={8} />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="phone2" label="Утас 2" placeholder="99112233" maxLength={8} />
                  </Grid.Col>
                  </Grid>
                </SectionCard>

                <SectionCard title="Хаяг" description="Байгууллагын байршил болон дэлгэрэнгүй хаяг">
                  <Grid gap="md">
                  <Grid.Col span={{ xs: 12, md: 4 }}>
                    <SelectField
                      name="province"
                      label="Аймаг/Хот"
                      placeholder="Сонгох"
                      options={provinceOptions}
                      searchable
                      clearable={false}
                      required
                      onChange={() => {
                        // Province changed: dependent selections are no longer valid.
                        setFieldValue("district", "");
                        setFieldValue("khoroo", "");
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 4 }}>
                    <SelectField
                      name="district"
                      label="Сум/Дүүрэг"
                      placeholder="Сонгох"
                      options={districtOptions}
                      searchable
                      clearable={false}
                      required
                      disabled={!values.province}
                      onChange={() => {
                        setFieldValue("khoroo", "");
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 4 }}>
                    <SelectField
                      name="khoroo"
                      label="Баг/Хороо"
                      placeholder="Сонгох"
                      options={khorooOptions}
                      searchable
                      clearable={false}
                      required
                      disabled={!values.district}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="khotkhonBair" label="Хотхон/Байр" placeholder="Хотхон/Байр" required />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="khoroolol" label="Хороолол" placeholder="Хороолол" required />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="khashaaDavkhar" label="Хашаа/Давхар" placeholder="Хашаа/Давхар" required />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextField name="khaalgaDugaar" label="Хаалганы дугаар" placeholder="Хаалганы дугаар" required />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextareaField name="businessAddress" label="Дэлгэрэнгүй хаяг" placeholder="Дэлгэрэнгүй хаяг" required />
                  </Grid.Col>
                  </Grid>
                </SectionCard>

                <Paper className={classes.footer} withBorder radius="md" p="md" bg="white">
                  <Group justify="flex-end">
                    <Button type="submit" loading={saving} disabled={!isDirty || hasErrors}>
                      Хадгалах
                    </Button>
                  </Group>
                </Paper>
              </Stack>
            );
          }}
        </Form>
          )}
        </Stack>
      </Container>
    </PageLayout>
  );
};

export default SettingsProfile;
